import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Icons, ThemeToggle } from './ui';
import { LangSwitcher } from './LangSwitcher';

const LoginForm: React.FC<{
  platform: 'mastodon' | 'misskey';
  placeholder: string;
}> = ({ platform, placeholder }) => {
  const { t } = useTranslation();
  const [domain, setDomain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(false);

  const Icon = platform === 'mastodon' ? Icons.Mastodon : Icons.Misskey;
  const sublabel =
    platform === 'mastodon'
      ? t('login.mastodonSub')
      : t('login.misskeySub');
  const color =
    platform === 'mastodon'
      ? 'bg-[#563ACC] hover:bg-[#4a2fb8]'
      : 'bg-[#86b300] hover:bg-[#749a00]';

  const handleChange = (v: string) => {
    setDomain(v);
    setError(v.includes('@') ? t('login.domainError') : null);
  };

  const handleLogin = async () => {
    if (!domain || error) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/v1/login/${platform}/${domain}`);
      if (!res.ok) {
        setToast(true);
        return;
      }
      const data = await res.json();
      window.location.href = data.login_url;
    } catch {
      setToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleLogin();
  };

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(false), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  return (
    <div className="relative">
      <Card className="!p-6">
        <div className="flex items-center gap-3 mb-5">
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center text-white ${color.split(' ')[0]}`}
          >
            <Icon width={22} height={22} />
          </div>
          <div>
            <h2 className="text-lg font-bold">{platform === 'mastodon' ? t('login.mastodonLogin') : t('login.misskeyLogin')}</h2>
            <p className="text-[12px] text-ink-500 dark:text-ink-400">
              {sublabel}
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div
            className={`flex items-center h-12 rounded-xl bg-ink-100 dark:bg-ink-850 ring-1 transition px-3 ${
              error
                ? 'ring-rose-400'
                : 'ring-transparent focus-within:ring-brand-blue-300'
            }`}
          >
            <Icons.Globe
              width={16}
              height={16}
              className="text-ink-400 mr-2 shrink-0"
            />
            <input
              type="text"
              value={domain}
              placeholder={placeholder}
              onChange={(e) => handleChange(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 bg-transparent outline-none text-[15px] placeholder:text-ink-400"
            />
          </div>
          {error && (
            <p className="text-[12px] text-rose-500">{error}</p>
          )}
          <button
            onClick={handleLogin}
            disabled={!domain || !!error || loading}
            className={`w-full h-11 rounded-xl text-white font-semibold text-[15px] flex items-center justify-center gap-2 transition ${color} disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            <Icon width={18} height={18} />
            {loading ? t('login.connecting') : (platform === 'mastodon' ? t('login.mastodonLogin') : t('login.misskeyLogin'))}
          </button>
        </div>
      </Card>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-ink-800 dark:bg-ink-100 text-white dark:text-ink-900 px-5 py-3 rounded-2xl shadow-pop text-[13px] font-medium">
          {t('login.serverError')}
        </div>
      )}
    </div>
  );
};

const Login: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-ink-50 dark:bg-ink-950 text-ink-800 dark:text-ink-100 flex flex-col items-center justify-center px-4">
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <LangSwitcher />
        <ThemeToggle />
      </div>
      <div className="w-full max-w-[460px]">
        <div className="text-center mb-10">
          <img src="/fediqb_banner_dark.svg" alt="FediQB" className="h-9 mx-auto mb-6 select-none dark:hidden" />
          <img src="/fediqb_banner.svg" alt="FediQB" className="h-9 mx-auto mb-6 select-none hidden dark:block" />
          <h1 className="text-3xl font-extrabold tracking-tight mb-2">{t('login.title')}</h1>
          <p className="text-[14px] text-ink-500 dark:text-ink-400">
            {t('login.subtitle')}
          </p>
        </div>

        <div className="space-y-4">
          <LoginForm platform="mastodon" placeholder={t('login.placeholderMastodon')} />
          <LoginForm platform="misskey" placeholder={t('login.placeholderMisskey')} />
        </div>

        <div className="mt-8 p-4 rounded-2xl bg-ink-100/60 dark:bg-ink-850/60 text-[12px] text-ink-500 dark:text-ink-400 leading-relaxed">
          <div className="flex items-start gap-2">
            <Icons.Lock width={14} height={14} className="mt-0.5 shrink-0 text-brand-green-500" />
            <span>
              {t('login.securityNote')}
            </span>
          </div>
        </div>

        <p className="mt-6 text-center text-[12px] text-ink-400">
          <a href="/" className="hover:text-ink-700 dark:hover:text-ink-200">
            ← {t('nav.backToTop')}
          </a>
        </p>
      </div>
    </div>
  );
};

export default Login;
